import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

type ErrorType = 'account_exists_local' | 'account_exists_oauth' | 'oauth_failed' | 'generic' | null;

interface ErrorState {
  type: ErrorType;
  email?: string;
  provider?: string;
  message?: string;
}

const OAuth2RedirectHandler: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { handleSocialLogin, isAuthenticated, user } = useAuth();
  const [error, setError] = useState<ErrorState | null>(null);
  const [isProcessing, setIsProcessing] = useState(true);
  const [isNewUser, setIsNewUser] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const token = searchParams.get('token');
    const errorParam = searchParams.get('error');
    const typeParam = searchParams.get('type');
    const emailParam = searchParams.get('email');
    const providerParam = searchParams.get('provider');
    const messageParam = searchParams.get('message');
    const isNewUserParam = searchParams.get('isNewUser');

    // Handle error cases from backend
    if (errorParam === 'account_exists') {
      setIsProcessing(false);
      if (typeParam === 'local') {
        setError({
          type: 'account_exists_local',
          email: emailParam || undefined,
        });
      } else if (typeParam === 'oauth') {
        setError({
          type: 'account_exists_oauth',
          email: emailParam || undefined,
          provider: providerParam || undefined,
        });
      }
      return;
    }

    if (errorParam === 'oauth_failed') {
      setIsProcessing(false);
      setError({
        type: 'oauth_failed',
        message: messageParam || 'OAuth authentication failed',
      });
      return;
    }

    // Check if this is a new user
    if (isNewUserParam === 'true') {
      setIsNewUser(true);
    }

    // Handle success case - token received
    if (token) {
      handleSocialLogin(token)
        .then(() => {
          // Wait a bit for state to propagate before navigating
          setTimeout(() => {
            setIsProcessing(false);
            // If new user, show welcome screen
            if (isNewUserParam === 'true') {
              setShowWelcome(true);
            } else {
              // Check for stored return URL (e.g., from checkout)
              const returnUrl = sessionStorage.getItem('oauth_return_url');
              sessionStorage.removeItem('oauth_return_url');
              navigate(returnUrl || '/orders', { replace: true });
            }
          }, 100);
        })
        .catch((err: Error) => {
          setIsProcessing(false);
          setError({
            type: 'generic',
            message: err.message || 'Failed to process social login. Please try again.',
          });
        });
    } else if (!errorParam) {
      // No token and no error - something went wrong
      setIsProcessing(false);
      setError({
        type: 'generic',
        message: 'No token found in redirect. Social login may have failed.',
      });
    }
  }, [location.search, handleSocialLogin, navigate]);

  // If authenticated and not processing, navigate to return URL or orders (for returning users)
  useEffect(() => {
    if (isAuthenticated && !isProcessing && !error && !isNewUser) {
      const returnUrl = sessionStorage.getItem('oauth_return_url');
      sessionStorage.removeItem('oauth_return_url');
      navigate(returnUrl || '/orders', { replace: true });
    }
  }, [isAuthenticated, isProcessing, error, isNewUser, navigate]);

  // Handle continue action for new users
  const handleContinue = () => {
    const returnUrl = sessionStorage.getItem('oauth_return_url');
    sessionStorage.removeItem('oauth_return_url');
    navigate(returnUrl || '/orders', { replace: true });
  };

  // Welcome screen for new OAuth users
  if (showWelcome && user) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100 dark:bg-gray-900 p-4">
        <div className="bg-white dark:bg-gray-800 p-8 rounded-lg shadow-xl text-center max-w-md">
          <div className="w-20 h-20 mx-auto mb-6 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center">
            <svg className="w-10 h-10 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-2">Welcome to Scan2Order!</h1>
          <p className="text-gray-600 dark:text-gray-300 mb-4">
            Your account has been created successfully.
          </p>
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 mb-6">
            <div className="text-left space-y-2">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                <span className="text-gray-700 dark:text-gray-300">{user.username || user.email}</span>
              </div>
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <span className="text-gray-700 dark:text-gray-300">{user.email}</span>
              </div>
            </div>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
            You can update your profile anytime from the settings menu.
          </p>
          <button
            onClick={handleContinue}
            className="w-full px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-semibold"
          >
            Get Started
          </button>
        </div>
      </div>
    );
  }

  // Account exists with local auth - prompt to login with password
  if (error?.type === 'account_exists_local') {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100 dark:bg-gray-900 p-4">
        <div className="bg-white dark:bg-gray-800 p-8 rounded-lg shadow-xl text-center max-w-md">
          <div className="w-16 h-16 mx-auto mb-4 bg-yellow-100 dark:bg-yellow-900 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-yellow-600 dark:text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-2">Account Already Exists</h1>
          <p className="text-gray-600 dark:text-gray-300 mb-4">
            An account with the email <span className="font-semibold">{error.email}</span> already exists.
          </p>
          <p className="text-gray-600 dark:text-gray-300 mb-6">
            Please login with your password, or use "Forgot Password" if you don't remember it.
          </p>
          <div className="space-y-3">
            <Link
              to="/login"
              className="block w-full px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              Login with Password
            </Link>
            <Link
              to="/forgot-password"
              state={{ email: error.email }}
              className="block w-full px-4 py-2 border border-purple-600 text-purple-600 dark:text-purple-400 rounded-lg hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-colors"
            >
              Forgot Password?
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Account exists with different OAuth provider
  if (error?.type === 'account_exists_oauth') {
    const providerName = error.provider ? error.provider.charAt(0).toUpperCase() + error.provider.slice(1) : 'another provider';
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100 dark:bg-gray-900 p-4">
        <div className="bg-white dark:bg-gray-800 p-8 rounded-lg shadow-xl text-center max-w-md">
          <div className="w-16 h-16 mx-auto mb-4 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-2">Different Login Method</h1>
          <p className="text-gray-600 dark:text-gray-300 mb-4">
            An account with the email <span className="font-semibold">{error.email}</span> already exists.
          </p>
          <p className="text-gray-600 dark:text-gray-300 mb-6">
            You previously signed up using <span className="font-semibold">{providerName}</span>. Please use the same login method.
          </p>
          <Link
            to="/login"
            className="block w-full px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            Go to Login
          </Link>
        </div>
      </div>
    );
  }

  // Generic OAuth error
  if (error?.type === 'oauth_failed' || error?.type === 'generic') {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100 dark:bg-gray-900 p-4">
        <div className="bg-white dark:bg-gray-800 p-8 rounded-lg shadow-xl text-center max-w-md">
          <div className="w-16 h-16 mx-auto mb-4 bg-red-100 dark:bg-red-900 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-red-600 dark:text-red-400 mb-4">Login Error</h1>
          <p className="text-gray-700 dark:text-gray-300 mb-6">{error.message}</p>
          <Link
            to="/login"
            className="block w-full px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            Go to Login
          </Link>
        </div>
      </div>
    );
  }

  // Loading state
  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100 dark:bg-gray-900">
      <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-purple-600"></div>
      <p className="mt-4 text-lg font-semibold text-gray-700 dark:text-gray-300">
        Processing your login...
      </p>
    </div>
  );
};

export default OAuth2RedirectHandler;
